import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FileSpreadsheet, Upload, Layers, ListTodo, Diamond, Users, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUS = { todo: ["Todo", "#8E8E93"], in_progress: ["In Progress", "#007AFF"], done: ["Done", "#34C759"] };

export const ImportExcelDialog = ({ open, onClose, customerId, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setProjectName(""); };
  const close = () => { reset(); onClose(); };

  const analyze = async (f) => {
    if (!f) return;
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/import/preview", fd);
      setPreview(data);
      setProjectName(data.project_name || f.name.replace(/\.xlsx?$/i, ""));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/import/commit", {
        customer_id: customerId, project_name: projectName.trim(), tasks: preview.tasks, milestones: preview.milestones, create_teams: true,
      });
      toast.success(`${data.task_count} task, ${data.milestone_count} milestone, ${data.column_count} statü kolonu oluşturuldu ✓`);
      reset();
      onImported(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const counts = preview ? preview.tasks.reduce((a, t) => ({ ...a, [t.status]: (a[t.status] || 0) + 1 }), {}) : {};
  void counts;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="import-excel-dialog" className="rounded-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2"><FileSpreadsheet size={18} className="text-[#34C759]" /> Excel'den Proje Oluştur</DialogTitle>
        </DialogHeader>

        {!preview ? (
          <label data-testid="import-dropzone" className="mt-2 flex flex-col items-center justify-center gap-3 h-44 rounded-2xl border-2 border-dashed border-slate-300 dark:border-white/15 bg-slate-50/60 dark:bg-white/5 hover:border-[#007AFF] hover:bg-[#007AFF]/5 cursor-pointer transition-colors text-center px-6">
            {loading ? <span className="w-6 h-6 rounded-full border-2 border-slate-300 border-t-[#0066CC] animate-spin" /> : <Upload size={22} className="text-slate-400" />}
            <div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{loading ? "Excel analiz ediliyor…" : "Proje planı Excel dosyasını seçin"}</p>
              <p className="text-xs text-slate-400 mt-1">"Proje Planı" sayfası: ID · Görev · Süre · Başlangıç · Bitiş · Sorumlu Ekip · Durum · Not</p>
            </div>
            <input data-testid="import-file-input" type="file" accept=".xlsx,.xlsm" className="hidden" disabled={loading} onChange={(e) => analyze(e.target.files?.[0])} />
          </label>
        ) : (
          <div className="space-y-4 mt-1">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Proje Adı</label>
              <input data-testid="import-project-name-input" value={projectName} onChange={(e) => setProjectName(e.target.value)}
                className="mt-1.5 w-full h-10 px-3.5 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white dark:focus:bg-white/15 transition-all" />
              <p className="text-[11px] text-slate-400 mt-1">{file?.name} · sayfa: {preview.sheet}</p>
            </div>

            <div data-testid="import-summary" className="grid grid-cols-4 gap-2">
              {[[ListTodo, preview.tasks.length, "Task"], [Layers, preview.groups.length, "Grup"], [Diamond, preview.milestones.length, "Milestone"], [Users, preview.team_names.length, "Ekip"]].map(([Icon, n, l]) => (
                <div key={l} className="rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/70 dark:border-white/10 p-3">
                  <Icon size={14} className="text-slate-400 mb-1.5" />
                  <p className="text-xl font-semibold font-display text-slate-900 dark:text-white leading-none">{n}</p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{l}</p>
                </div>
              ))}
            </div>
            {preview.stats && (
              <p data-testid="import-row-check" className="text-[11px] text-slate-500 flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-[#34C759]" />
                Excel'de {preview.stats.data_rows} satır okundu: {preview.stats.root_rows} proje başlığı + {preview.stats.header_rows} faz/modül başlığı + <b>{preview.stats.task_rows} görev</b> · {preview.stats.dated_tasks}/{preview.stats.task_rows} görevde tarih var
              </p>
            )}

            <div className="flex flex-wrap gap-1.5 text-xs">
              {(preview.status_labels || []).map((label) => {
                const key = preview.tasks.find((t) => (t.status_label || t.status) === label)?.status || "todo";
                return <span key={label} data-testid="import-status-chip" className="px-2 py-1 rounded-md font-semibold" style={{ background: `${STATUS[key][1]}15`, color: STATUS[key][1] }}>{label}: {preview.tasks.filter((t) => (t.status_label || t.status) === label).length}</span>;
              })}
              {preview.team_names.map((t) => (
                <span key={t} className="px-2 py-1 rounded-md bg-[#AF52DE]/10 text-[#AF52DE] font-medium flex items-center gap-1"><Users size={10} /> {t}</span>
              ))}
            </div>

            {preview.warnings.length > 0 && (
              <div data-testid="import-warnings" className="rounded-xl bg-[#FF9500]/10 border border-[#FF9500]/30 p-3 text-xs text-[#B36B00] dark:text-[#FFB340] space-y-0.5">
                <p className="font-semibold flex items-center gap-1"><AlertTriangle size={12} /> Uyarılar</p>
                {preview.warnings.slice(0, 5).map((w, i) => <p key={i}>• {w}</p>)}
              </div>
            )}

            <div className="rounded-xl border border-slate-200/70 dark:border-white/10 overflow-hidden">
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 dark:divide-white/5">
                {preview.tasks.map((t, i) => (
                  <div key={i} data-testid="import-task-row" className="flex items-center gap-3 px-3 py-2 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: STATUS[t.status][1] }} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-slate-800 dark:text-slate-100 truncate">{t.is_milestone && <Diamond size={10} className="inline mr-1 text-[#AF52DE] -mt-0.5" />}{t.title}</span>
                      <span className="block text-[10px] text-slate-400 truncate">{t.group}{t.status_label ? ` · ${t.status_label}` : ""}</span>
                    </span>
                    <span className={`text-[10px] shrink-0 ${t.start_date ? "text-slate-400" : "text-[#FF9500] font-semibold"}`}>{t.start_date ? `${t.start_date} → ${t.due_date}` : "tarih yok"}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button data-testid="import-back-button" onClick={reset} disabled={loading}
                className="h-10 px-4 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">Başka dosya</button>
              <button data-testid="import-commit-button" onClick={commit} disabled={loading || !projectName.trim()}
                className="flex-1 h-10 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60">
                {loading ? <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <CheckCircle2 size={15} />}
                Projeyi Oluştur ({preview.tasks.length} task)
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportExcelDialog;
